/**
 * OnboardingPage.js — RC 1.43.C.1 single-step restructure (2026-04-29).
 *
 * "CV-first" 1-step wizard:
 *
 *   STEP 1 — "Hello, {first_name}" + CV upload (mandatory)
 *            → uploads to Supabase Storage (existing `Cvs` bucket)
 *            → on Continue, persists `base_cvs` row, writes the 3
 *              required `consents` rows (privacy_policy, cv_processing,
 *              job_search) silently behind the inline "By continuing…"
 *              sentence, and routes to /onboarding/plan for plan
 *              selection.
 *
 * RC 1.43.C.1 — Preferences are no longer captured at onboarding.
 * They are managed exclusively from /account?tab=preferences (RC
 * 1.43.B unified preference centre). A dashboard nudge prompts
 * candidates to set them after activation.
 *
 * Hard rails preserved (RC 1.38–1.43.C.1):
 *   • RC 1.38.D-hotfix: profileChecked gate (no remount loop on
 *     refreshProfile()).
 *   • RC 1.38.D2-hotfix: narrow gate (only SIGNED_IN /
 *     INITIAL_SESSION re-gate).
 *   • RC 1.38.D2-hotfix: sessionStorage-backed wizard state keyed
 *     by user.id. Schema bumped to v: 3 — v: 1 / v: 2 snapshots
 *     from the old 4-step / 2-step flows are silently ignored.
 *   • RC 1.41 P0.1.C-r9: CV upload is MANDATORY (Skip retired).
 *   • Zero schema changes. Zero LLM calls. Zero usage deduction.
 *   • Stripe / employer / ATS / live-mode untouched.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { apiFetch } from '@/lib/api';
import {
  Loader2, ArrowRight, AlertCircle, Sparkles, FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import OnboardingShell from '@/components/onboarding/OnboardingShell';

import CvStep from './onboarding/CvStep.jsx';

const STEPS = [
  { key: 'cv', subtitle: "Upload your CV — we'll read the rest." },
];

const STORAGE_VERSION = 3; // RC 1.43.C.1 — bumped from v2 (RC 1.39 2-step flow)
const REQUIRED_CONSENTS = ['privacy_policy', 'cv_processing', 'job_search'];

export default function OnboardingPage() {
  const { user, hasProfile, refreshProfile } = useAuth();
  const navigate = useNavigate();

  // ─────────────────────────────────────────────────────────────
  // Persistence — RC 1.38.D2 carried forward, schema v2.
  // ─────────────────────────────────────────────────────────────
  const storageKey = user?.id ? `ndor-onboarding-${user.id}` : null;
  const persisted = useMemo(() => {
    if (!storageKey) return null;
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      // v1 snapshots came from the old 4-step flow with `profile`
      // and `consents` slices. Discard them silently.
      if (parsed?.v !== STORAGE_VERSION) return null;
      return parsed;
    } catch { return null; }
  }, [storageKey]);

  const [currentStep, setCurrentStep] = useState(() => persisted?.currentStep ?? 0);
  const [cvData, setCvData]           = useState(() => persisted?.cvData ?? null);
  const [saving, setSaving]   = useState(false);
  const [completed, setCompleted] = useState(false);
  const [saveError, setSaveError] = useState('');

  // Persist on every meaningful change (transient flags excluded).
  useEffect(() => {
    if (!storageKey || completed) return;
    try {
      sessionStorage.setItem(storageKey, JSON.stringify({
        v: STORAGE_VERSION,
        currentStep, cvData,
        savedAt: Date.now(),
      }));
    } catch { /* quota / private-mode — non-fatal */ }
  }, [storageKey, currentStep, cvData, completed]);

  // Clear snapshot on completion.
  useEffect(() => {
    if (completed && storageKey) {
      try { sessionStorage.removeItem(storageKey); } catch { /* */ }
    }
  }, [completed, storageKey]);

  // ─────────────────────────────────────────────────────────────
  // Routing rules
  // ─────────────────────────────────────────────────────────────

  // Employer branch: doctrine-locked under RC 1.62 employer-auth
  // (F-D6, 2026-05-29). Employer signups MUST NOT be walked through
  // the `/onboarding/employer` org-create wizard — that wizard is an
  // ADMIN action (admin creates an organisation and assigns an
  // owner's email). The only legitimate landing for an employer
  // signup is `/employer` → either the org workspace (member) OR
  // the empty-workspace stub (no membership yet, awaiting invite).
  //
  // We also force-bounce them OUT of this candidate wizard if they
  // are employer-only — they have no business filling in a CV here.
  useEffect(() => {
    if (!user?.id || hasProfile) return;
    let cancelled = false;
    (async () => {
      try {
        const access = await apiFetch('/api/auth/me/access');
        if (cancelled) return;
        if (access?.employer_access === true && access?.candidate_access !== true) {
          // Employer-only → land directly on the employer surface.
          // `hasProfile` will be re-resolved by AuthContext on the
          // next refresh: for employer-only users the consents gate
          // is skipped (see AuthContext.checkOnboardingComplete).
          navigate('/employer', { replace: true });
        }
      } catch { /* fail open — leave the user on the candidate wizard */ }
    })();
    return () => { cancelled = true; };
  }, [user?.id, hasProfile, navigate]);

  // RC 1.48.5 — Defensive auto-hydration of `cvData` from the user's
  // already-uploaded primary base CV. The Source-CV upload happens
  // mid-flow and is persisted server-side immediately, but the React
  // state lives in sessionStorage. Edge cases — incognito close,
  // devtools clear, very fast multi-tab navigation — can drop the
  // sessionStorage row even though the DB still carries the row.
  // Without this hydration, the user is stranded on /onboarding with
  // a "Please upload your CV to continue" guard despite their CV
  // being fully uploaded server-side.
  //
  // Strict scope: this is a UX-recovery shim. The CV upload remains
  // mandatory; the consents step still runs. We hydrate the MINIMAL
  // shape `{cv_id, original_filename}` — CvStep's existing
  // `useEffect` then auto-fetches the rest via `/api/base-cvs/{id}`.
  // Zero touches to scoring, governance, prompts, charge gate, etc.
  useEffect(() => {
    if (!user?.id) return;
    if (cvData?.cv_id) return; // already hydrated, nothing to do
    if (saving || completed) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await apiFetch('/api/profile/me');
        if (cancelled) return;
        const primary = r?.primary_cv;
        if (primary?.id) {
          setCvData({
            cv_id: primary.id,
            original_filename: primary.original_filename || 'Source CV',
            // text_length / parse_status flow in via CvStep's child
            // hydration effect at /api/base-cvs/{cv_id}.
            text_length: 0,
            parse_status: 'success',
            is_primary: true,
          });
        }
      } catch { /* fail open — leave cvData null, user can re-upload */ }
    })();
    return () => { cancelled = true; };
  }, [user?.id, cvData?.cv_id, saving, completed]);

  // Once onboarded (consents row exists), route to plan selection.
  // RC 1.43.C.1 — direct to /onboarding/plan rather than /dashboard.
  //
  // RC 1.44.H7 — gate the `hasProfile` shortcut. The previous
  // implementation produced a brief dashboard flash for brand-new
  // users: `await refreshProfile()` flipped `hasProfile=true` BEFORE
  // `setCompleted(true)` propagated, so the `hasProfile` branch fired
  // `/dashboard` first and the setTimeout 1.4s later overrode with
  // `/onboarding/plan`. The shortcut now only applies when the user
  // lands on /onboarding fresh (no in-flight upload, not saving, not
  // completed) — a real returning, already-onboarded user.
  useEffect(() => {
    // RC 1.44.O1.A.1 — interstitial deleted, so the previous 1.4 s
    // timeout that gated the navigate-after-completed is now pure
    // dead air. Navigate immediately so the unified shell flips
    // straight from Step 1 to Step 2.
    //
    // RC 1.46.ONBOARDING-BACK (2026-05-02) — stamp nav-state
    // `from: '/onboarding'` so OnboardingPlanPage can detect the
    // first-run cohort and lock its Back button to `/onboarding`
    // (preventing pre-plan-select bypass to /account or /dashboard).
    if (completed) {
      navigate('/onboarding/plan', {
        replace: true,
        state: { from: '/onboarding' },
      });
      return;
    }
    if (hasProfile && !cvData && !saving) {
      navigate('/dashboard', { replace: true });
    }
  }, [completed, hasProfile, navigate, cvData, saving]);

  // ─────────────────────────────────────────────────────────────
  // Friendly hello — read first name from auth metadata or storage.
  // ─────────────────────────────────────────────────────────────
  const firstName = (
    user?.user_metadata?.first_name
    || user?.user_metadata?.full_name?.split(' ')[0]
    || ''
  ).trim() || 'there';

  // ─────────────────────────────────────────────────────────────
  // Save helpers
  // ─────────────────────────────────────────────────────────────

  async function getAuthUserId() {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.user?.id || null;
  }

  /**
   * RC 1.40: extraction-first uploads create the `base_cvs` row
   * server-side as part of the multipart POST, so by the time the
   * user taps Continue the row already exists with parsed_text +
   * parsed_json + is_primary. We just fire the legacy /parse hook
   * for any future enrichment hooks that depend on it (no-op today).
   */
  async function persistCvRow() {
    if (!cvData?.cv_id) return { ok: true, cv_id: null };
    return { ok: true, cv_id: cvData.cv_id };
  }

  /** Silently write the 3 required consent rows. */
  async function persistConsents() {
    const userId = await getAuthUserId();
    if (!userId) return { ok: false, msg: 'Session expired.' };

    const now = new Date().toISOString();
    const rows = REQUIRED_CONSENTS.map((type) => ({
      user_id: userId,
      consent_type: type,
      accepted: true,
      policy_version: '1.0',
      accepted_at: now,
    }));

    const { error: err } = await supabase.from('consents').insert(rows);
    if (err) return { ok: false, msg: `Couldn't record consent: ${err.message}` };
    return { ok: true };
  }

  // ─────────────────────────────────────────────────────────────
  // Step navigation
  // ─────────────────────────────────────────────────────────────

  async function handleNext() {
    setSaveError('');
    if (!user?.id) {
      setSaveError('You are not signed in. Please refresh.');
      return;
    }

    setSaving(true);
    try {
      // RC 1.43.C.1 — single-step onboarding. CV upload is mandatory;
      // consents are written immediately after a successful CV; user
      // is then routed to /onboarding/plan.
      if (!cvData?.cv_id) {
        const msg = 'Please upload your CV to continue. This becomes your Source CV — every tailored CV variant and cover letter is generated from it.';
        setSaveError(msg);
        toast.error(msg);
        return;
      }
      const r = await persistCvRow();
      if (!r.ok) { setSaveError(r.msg); toast.error(r.msg); return; }

      const cr = await persistConsents();
      if (!cr.ok) { setSaveError(cr.msg); toast.error(cr.msg); return; }

      await refreshProfile();
      setCompleted(true);
    } catch (err) {
      const msg = `Unexpected error: ${err?.message || 'Unknown'}.`;
      console.error('[Onboarding] uncaught:', err);
      setSaveError(msg); toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  // ─────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────
  // RC 1.44.O1.A.1 — `completed === true` interstitial removed.
  // Previously rendered a full-screen "You're in." success card for
  // 1.4 s between Step 1 (CV upload) and Step 2 (plan selection),
  // breaking the unified shell continuity. The progress bar moving
  // 50% → 100% is enough confirmation. Step 1 keeps rendering until
  // the navigate fires.
  const ctaLabel = 'Finish setup';

  return (
    <OnboardingShell step={1} totalSteps={2} stepLabel="Upload your Source CV" testid="onboarding-page">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-3xl sm:text-4xl font-serif-display font-semibold tracking-tight text-ndor-ink" data-testid="onboarding-hello">
          Hello, <span className="text-ndor-gold">{firstName}</span>
        </h1>
        <p className="text-sm text-ndor-ink-secondary mt-2 mb-6 font-manrope">
          {STEPS[0].subtitle}
        </p>

        {saveError && (
          <div
            className="mb-5 rounded-lg bg-ndor-error-light border border-ndor-error-border p-3.5 flex items-start gap-2.5"
            data-testid="onboarding-save-error"
          >
            <AlertCircle className="h-4 w-4 text-ndor-error shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-ndor-error">Save failed</p>
              <p className="text-sm text-ndor-ink-secondary mt-0.5">{saveError}</p>
            </div>
          </div>
        )}

        {/* RC 1.44.O1.A — CV upload card lifted to plan-card visual
            polish: gold-accent border, icon header, generous padding,
            so the candidate's first surface feels as deliberate as
            Step 2's plan cards. */}
        <div
          className="rounded-2xl border border-ndor-gold/40 bg-ndor-surface shadow-sm p-5 sm:p-6"
          data-testid="onboarding-cv-card"
        >
          <div className="flex items-center gap-2.5 mb-4">
            <div className="h-9 w-9 rounded-lg bg-ndor-gold/10 flex items-center justify-center">
              <FileText className="h-4 w-4 text-ndor-gold" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-ndor-ink font-outfit">
                Upload your Source CV
              </h2>
              <p className="text-xs text-ndor-ink-muted font-manrope">
                One CV becomes your truth. Encrypted at rest, deletable on demand.
              </p>
            </div>
          </div>
          <CvStep data={cvData} onChange={setCvData} />
        </div>

        {/* RC 1.43.C.1 — Inline terms acceptance relocated from old
            Step 2 to the only step. Verbatim copy preserved. */}
        <p
          className="mt-5 text-xs text-ndor-ink-muted leading-relaxed flex items-start gap-1.5 font-manrope"
          data-testid="onboarding-terms-line"
        >
          <Sparkles className="h-3.5 w-3.5 mt-0.5 shrink-0 text-ndor-gold" />
          <span>
            By tapping <strong>{ctaLabel}</strong>, you agree to NDORFlow's{' '}
            <a
              href="/terms"
              target="_blank"
              rel="noreferrer"
              className="text-ndor-ink hover:underline"
              data-testid="onboarding-terms-tos"
            >Terms of Service</a>,{' '}
            <a
              href="/privacy"
              target="_blank"
              rel="noreferrer"
              className="text-ndor-ink hover:underline"
              data-testid="onboarding-terms-privacy"
            >Privacy Policy</a>, and{' '}
            <a
              href="/refund"
              target="_blank"
              rel="noreferrer"
              className="text-ndor-ink hover:underline"
              data-testid="onboarding-terms-refund"
            >Refund Policy</a>{' '}
            and consent to NDORFlow processing your CV and searching jobs on your behalf.
          </span>
        </p>

        {/* Sticky footer relocated inline at the bottom of content
            so the shell's centred chrome stays visually consistent
            with Step 2 (which has no separate footer). */}
        <div
          className="mt-8 flex items-center justify-end gap-3"
          data-testid="onboarding-footer"
        >
          <Button
            onClick={handleNext}
            disabled={saving}
            className="min-w-[140px]"
            data-testid="onboarding-next-button"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}
            {!saving && ctaLabel}
            {!saving && <ArrowRight className="h-4 w-4 ml-1.5" />}
          </Button>
        </div>
      </div>
    </OnboardingShell>
  );
}
